import React, { createContext, useContext, useEffect, useState } from "react";
import { CssBaseline } from "@mui/material";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";

import lightPalette from "../theme/palette";
import typography from "../theme/typography";

// Тип для режима темы
export type ThemeMode = "light" | "dark";

// Интерфейс контекста темы
interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

// Создание контекста темы
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Хук для использования контекста темы
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(
      "useThemeContext должен использоваться внутри ThemeProvider"
    );
  }
  return context;
};

// Создание темной палитры на основе светлой
const createDarkPalette = () => {
  return {
    ...lightPalette,
    mode: "dark",
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B0B0B0",
    },
  };
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Провайдер темы
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Получаем сохраненную тему из localStorage или используем светлую тему по умолчанию
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem("themeMode");
    return (savedMode as ThemeMode) || "light";
  });

  // Создаем тему в зависимости от режима
  const theme = React.useMemo(() => {
    return createTheme({
      components: {
        // Используем те же компоненты из основной темы
        ...createTheme().components,
      },
      palette: mode === "light" ? lightPalette : createDarkPalette(),
      typography,
    });
  }, [mode]);

  // Переключение темы
  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  // Сохраняем тему в localStorage при изменении
  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
